import React from 'react';
import { DollarSign, Calendar, Clock } from 'lucide-react';
import { formatPrice, getTzAbbr, formatEventDateByPref } from '../utils/formatting';

// (24 Oct 2026, 8.40a, decision 100) The example shows today exactly as event dates
// will look: the Date Format, with the day of the week unless it is unticked. (Was a
// local formatDate without the day, now unused.)
const localTodayIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

// (26 Sep 2026) Currency and Language hidden for now (Neil): there are no
// prices to show and the interface isn't translated. The stored values are
// untouched and still sent on Save. Set to true to bring both back.
const SHOW_CURRENCY_AND_LANGUAGE = false;

// (30 Sep 2026, H16) Plain members never create calendars, and the personal
// time-zone setting affects nothing else (the calendar feed, emails and event
// cards use the event's or the calendar's own zone; phones convert to their
// own clock). So members see, read-only, the time zone their DEVICE is on —
// with a warning when it differs from the calendar's, since their phone's
// calendar will then show events in that other zone (e.g. a phone still on
// London time shows an 18:30 event in France as 17:30).
function hhmmInZone(zone) {
  // 18:30 on the next day, in the calendar's zone, shown in the device's zone.
  try {
    const now = new Date();
    const probe = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 12, 0));
    const fmt = (tz) => new Intl.DateTimeFormat('en-GB', { timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false }).format(probe);
    const [ch, cm] = fmt(zone).split(':').map(Number);
    const [dh, dm] = fmt(undefined).split(':').map(Number);
    const diff = (dh * 60 + dm) - (ch * 60 + cm);
    const t = ((18 * 60 + 30 + diff) % 1440 + 1440) % 1440;
    return `${String(Math.floor(t / 60)).padStart(2, '0')}:${String(t % 60).padStart(2, '0')}`;
  } catch (_) {
    return null;
  }
}

const DeviceTimezoneLine = ({ calendarTimezone, calendarName }) => {
  let device = null;
  try { device = Intl.DateTimeFormat().resolvedOptions().timeZone || null; } catch (_) { device = null; }
  if (!device) return null;
  const today = new Date().toISOString().slice(0, 10);
  const label = (tz) => {
    const abbr = getTzAbbr(tz, today);
    return abbr ? `${tz} (${abbr})` : tz;
  };
  const same = !calendarTimezone || hhmmInZone(calendarTimezone) === '18:30';
  return (
    <div className="mb-4">
      <label className="flex items-center gap-2 text-sm font-medium mb-2">
        <Clock className="w-4 h-4" />
        Your device's time zone
      </label>
      <p className="text-sm text-gray-800">{label(device)}</p>
      {same ? (
        <p className="text-xs text-gray-500 mt-1">
          Event times in your calendar will show as they appear in the {calendarName ? `${calendarName} calendar` : 'group\'s calendar'}.
        </p>
      ) : (
        <p className="text-xs text-amber-700 mt-1">
          This calendar's events are in {label(calendarTimezone)}. Your calendar will show them in your device's time
          instead, so an 18:30 event appears as {hhmmInZone(calendarTimezone) || 'a different time'}. If you're in the same
          place as the event, check that your phone's Date &amp; Time is set automatically.
        </p>
      )}
    </div>
  );
};

const PreferencesTab = ({ preferences, setPreferences, setHasChanges, showMessageTimes = false, memberView = false, calendarTimezone = null, calendarName = '' }) => {
  const handleChange = (field, value) => {
    setPreferences(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-4">Regional Settings</h3>
        
        {/* Currency */}
        {SHOW_CURRENCY_AND_LANGUAGE && (
        <div className="mb-4">
          <label className="flex items-center gap-2 text-sm font-medium mb-2">
            <DollarSign className="w-4 h-4" />
            Currency
          </label>
          <select 
            value={preferences.currency}
            onChange={(e) => handleChange('currency', e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
          >
            <optgroup label="Americas">
              <option value="USD">USD - US Dollar ($)</option>
              <option value="CAD">CAD - Canadian Dollar (C$)</option>
            </optgroup>
            <optgroup label="Europe">
              <option value="EUR">EUR - Euro (€)</option>
              <option value="GBP">GBP - British Pound (£)</option>
              <option value="CHF">CHF - Swiss Franc (CHF)</option>
            </optgroup>
            <optgroup label="Asia Pacific">
              <option value="JPY">JPY - Japanese Yen (¥)</option>
              <option value="CNH">CNH - Chinese Renminbi (¥)</option>
              <option value="HKD">HKD - Hong Kong Dollar (HK$)</option>
              <option value="AUD">AUD - Australian Dollar (A$)</option>
              <option value="NZD">NZD - New Zealand Dollar (NZ$)</option>
            </optgroup>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Used for displaying subscription prices (e.g., {formatPrice(4.99, preferences.currency)}/month)
          </p>
        </div>
        )}

        {/* Date Format */}
        <div className="mb-4">
          <label className="flex items-center gap-2 text-sm font-medium mb-2">
            <Calendar className="w-4 h-4" />
            Date Format
          </label>
          <select 
            value={preferences.dateFormat}
            onChange={(e) => handleChange('dateFormat', e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
          >
            <option value="DD-MMM-YYYY">DD-MMM-YYYY (15-Feb-2026)</option>
            <option value="MM/DD/YYYY">MM/DD/YYYY (02/15/2026)</option>
            <option value="DD/MM/YYYY">DD/MM/YYYY (15/02/2026)</option>
            <option value="YYYY-MM-DD">YYYY-MM-DD (2026-02-15)</option>
          </select>
          {/* (24 Oct 2026, 8.40a, decision 100) Day of the week with event dates, on by default. */}
          <label className="flex items-center gap-2 text-sm text-gray-700 mt-3 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={preferences.showWeekday !== false}
              onChange={(e) => handleChange('showWeekday', e.target.checked)}
              className="w-4 h-4"
            />
            Show the day of the week with event dates
          </label>
          <p className="text-xs text-gray-500 mt-1">
            Example: {formatEventDateByPref(localTodayIso(), preferences.dateFormat, preferences.showWeekday !== false)}
          </p>
        </div>

        {/* Timezone — (30 Sep 2026, H16) administrators only; members get the device line */}
        {memberView ? (
          <DeviceTimezoneLine calendarTimezone={calendarTimezone} calendarName={calendarName} />
        ) : (
        <div className="mb-4">
          <label className="flex items-center gap-2 text-sm font-medium mb-2">
            <Clock className="w-4 h-4" />
            Timezone
          </label>
          <select 
            value={preferences.timezone}
            onChange={(e) => handleChange('timezone', e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
          >
            <optgroup label="Common">
              <option value="UTC">UTC</option>
              <option value="Europe/Paris">Europe/Paris (CET)</option>
              <option value="Europe/London">Europe/London (GMT)</option>
              <option value="America/New_York">America/New_York (EST)</option>
              <option value="America/Los_Angeles">America/Los_Angeles (PST)</option>
            </optgroup>
            <optgroup label="Europe">
              <option value="Europe/Berlin">Europe/Berlin (CET)</option>
              <option value="Europe/Madrid">Europe/Madrid (CET)</option>
              <option value="Europe/Rome">Europe/Rome (CET)</option>
              <option value="Europe/Amsterdam">Europe/Amsterdam (CET)</option>
            </optgroup>
            <optgroup label="Americas">
              <option value="America/Chicago">America/Chicago (CST)</option>
              <option value="America/Denver">America/Denver (MST)</option>
              <option value="America/Toronto">America/Toronto (EST)</option>
            </optgroup>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Used as the time zone of any new calendar you create. Each calendar's own time zone is under Rename Calendar.
          </p>
        </div>
        )}

        {/* (26 Sep 2026) Pop-up message times — administrators only
            (members get the defaults, 4 and 8 seconds). */}
        {showMessageTimes && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-2">Messages</label>
            {/* (26 Sep 2026) A three-column grid — wording, box, "seconds" — so
                both boxes line up however the wording wraps. */}
            <div className="grid grid-cols-[1fr_auto_auto] items-center gap-x-2 gap-y-2 text-sm text-gray-700">
              <label htmlFor="pref-message-seconds">Ordinary messages stay on screen for</label>
              <input
                id="pref-message-seconds"
                type="number"
                min="2"
                max="30"
                value={preferences.messageSeconds}
                onChange={(e) => handleChange('messageSeconds', e.target.value)}
                className="w-16 border rounded-lg px-2 py-1"
              />
              <span>seconds</span>
              <label htmlFor="pref-error-message-seconds">Error messages stay on screen for</label>
              <input
                id="pref-error-message-seconds"
                type="number"
                min="2"
                max="30"
                value={preferences.errorMessageSeconds}
                onChange={(e) => handleChange('errorMessageSeconds', e.target.value)}
                className="w-16 border rounded-lg px-2 py-1"
              />
              <span>seconds</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Allowable range is from 2 to 30 seconds. Error messages can also be closed early with the ×.
            </p>
          </div>
        )}

        {/* Language */}
        {SHOW_CURRENCY_AND_LANGUAGE && (
        <div className="mb-4">
          <label className="flex items-center gap-2 text-sm font-medium mb-2">
            Language
          </label>
          <select 
            value={preferences.language}
            onChange={(e) => handleChange('language', e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
          >
            <option value="en">English</option>
            <option value="fr">Français</option>
          </select>
          <p className="text-xs text-gray-500 mt-1">
            Interface language
          </p>
        </div>
        )}
      </div>
    </div>
  );
};

export default PreferencesTab;
